/**
 * Assessment Grades Report Page
 * Shows learner grades for an assessment with multi-class selection support
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Alert, CircularProgress,
  Breadcrumbs, Link as MuiLink, Checkbox, FormControlLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Card, CardContent, Stack, Divider, IconButton, Tooltip
} from '@mui/material';
import {
  Home, Assessment, Visibility, FileDownload, SelectAll, Clear
} from '@mui/icons-material';
import axios from 'axios';
import * as XLSX from 'xlsx';

interface SubsectionTotal {
  score: number;
  max_score: number;
}

interface GradeData {
  submission_id: number;
  learner_id: number;
  learner_name: string;
  student_id: string;
  class_id: number;
  class_name: string;
  ai_score: number | null;
  teacher_score: number | null;
  status: string;
  submitted_at: string | null;
  subsection_totals: Record<string, SubsectionTotal>;
}

interface ClassStatistics {
  class_id: number;
  class_name: string;
  assignment_id: number;
  total_learners: number;
  marked: number;
  average_score: number | null;
  highest_score: number | null;
  lowest_score: number | null;
}

interface RelatedAssignment {
  id: number;
  title: string;
  class_id: number;
  class_name: string;
  due_date: string;
}

interface AssignedClass {
  id: number;
  name: string;
  grade_level: number;
  subject: string;
}

interface AssignmentInfo {
  id: number;
  title: string;
  class_id: number;
  class_name: string;
  due_date: string;
  max_points: number;
  rubric_id: number | null;
  rubric_name: string | null;
  assigned_classes?: AssignedClass[];
}

interface RubricSection {
  section: string;
  name: string;
  max_points: number;
  criteria_count: number;
}

interface GradesResponse {
  assignment: AssignmentInfo;
  related_assignments: RelatedAssignment[];
  rubric_sections: RubricSection[];
  grades: GradeData[];
  class_statistics: ClassStatistics[];
}

const AssessmentGradesPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Data state
  const [gradesData, setGradesData] = useState<GradesResponse | null>(null);
  const [selectedClassIds, setSelectedClassIds] = useState<Set<number>>(new Set());
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load grades data — fetch ONCE, then filter client-side (no re-fetch on class toggle)
  useEffect(() => {
    const fetchGrades = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);

        const response = await axios.get<GradesResponse>(`/api/assignments/${id}/grades/`);
        setGradesData(response.data);

        // Auto-select all assigned classes on first load
        const data = response.data;
        if (data.assignment) {
          const assignedClassIds = data.assignment.assigned_classes
            ? data.assignment.assigned_classes.map(c => c.id)
            : [data.assignment.class_id];
          setSelectedClassIds(new Set(assignedClassIds));
        }
        setInitialLoadDone(true);
      } catch (err: any) {
        console.error('Error loading grades:', err);
        setError(err.response?.data?.error || 'Failed to load grade report');
      } finally {
        setLoading(false);
      }
    };

    fetchGrades();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Get all available classes (current + related)
  const getAllClasses = (): ClassStatistics[] => {
    if (!gradesData) return [];
    return gradesData.class_statistics;
  };

  // Toggle class selection
  const handleClassToggle = (classId: number) => {
    const newSelected = new Set(selectedClassIds);
    if (newSelected.has(classId)) {
      newSelected.delete(classId);
    } else {
      newSelected.add(classId);
    }
    setSelectedClassIds(newSelected);
  };

  // Select all classes
  const handleSelectAll = () => {
    const allClassIds = getAllClasses().map(c => c.class_id);
    setSelectedClassIds(new Set(allClassIds));
  };

  // Deselect all classes
  const handleDeselectAll = () => {
    setSelectedClassIds(new Set());
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (!gradesData) return;

    const rubricSections = gradesData.rubric_sections ?? [];

    // For rubric-based assessments use rubric_sections; for test papers derive
    // column keys from the union of subsection_totals keys across all grades.
    const sectionKeys: string[] = rubricSections.length > 0
      ? rubricSections.map(s => s.section)
      : Array.from(new Set(gradesData.grades.flatMap(g => Object.keys(g.subsection_totals ?? {}))))
          .sort((a, b) => (isNaN(Number(a)) || isNaN(Number(b)) ? a.localeCompare(b) : Number(a) - Number(b)));

    const sectionHeaders = sectionKeys.map(key => `Q${key}`);

    const headers = ['Class', 'Learner Name', 'Student ID', ...sectionHeaders, 'AI Score', 'Teacher Score', 'Final Score', 'Status', 'Submitted At'];
    const rows = gradesData.grades.map(grade => {
      const sectionScores = sectionKeys.map(key => {
        const totals = grade.subsection_totals?.[key];
        if (!totals) return '';
        return `${totals.score}/${totals.max_score}`;
      });
      return [
        grade.class_name,
        grade.learner_name,
        grade.student_id,
        ...sectionScores,
        grade.ai_score ?? '',
        grade.teacher_score ?? '',
        grade.teacher_score ?? grade.ai_score ?? '',
        grade.status,
        grade.submitted_at ? new Date(grade.submitted_at).toLocaleString() : ''
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `grades_${gradesData.assignment.title.replace(/[^a-z0-9]/gi, '_')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Export to XLSX — one sheet per class
  const handleExportXLSX = () => {
    if (!gradesData) return;

    const rubricSections = gradesData.rubric_sections ?? [];
    const sectionKeys: string[] = rubricSections.length > 0
      ? rubricSections.map((s: RubricSection) => s.section)
      : Array.from(new Set(gradesData.grades.flatMap((g: GradeData) => Object.keys(g.subsection_totals ?? {}))))
          .sort((a: string, b: string) => (isNaN(Number(a)) || isNaN(Number(b)) ? a.localeCompare(b) : Number(a) - Number(b)));

    const headers = ['Learner Name', 'Student ID', ...sectionKeys.map(k => `Q${k}`), 'AI Score', 'Teacher Score', 'Final Score', 'Status', 'Submitted At'];

    const gradesByClass: Record<string, GradeData[]> = {};
    for (const grade of gradesData.grades) {
      const cls = grade.class_name || 'Unknown';
      if (!gradesByClass[cls]) gradesByClass[cls] = [];
      gradesByClass[cls].push(grade);
    }

    const workbook = XLSX.utils.book_new();

    for (const [className, classGrades] of Object.entries(gradesByClass)) {
      const rows = classGrades.map((grade: GradeData) => {
        const sectionScores = sectionKeys.map(key => {
          const t = grade.subsection_totals?.[key];
          return t ? t.score : '';
        });
        return [
          grade.learner_name,
          grade.student_id,
          ...sectionScores,
          grade.ai_score ?? '',
          grade.teacher_score ?? '',
          grade.teacher_score ?? grade.ai_score ?? '',
          grade.status,
          grade.submitted_at ? new Date(grade.submitted_at).toLocaleString() : '',
        ];
      });

      const sheetData = [headers, ...rows];
      const sheet = XLSX.utils.aoa_to_sheet(sheetData);

      // Auto-width columns
      const colWidths = headers.map((h, i) => ({
        wch: Math.max(h.length, ...rows.map(r => String(r[i] ?? '').length)) + 2,
      }));
      sheet['!cols'] = colWidths;

      // Sheet names max 31 chars, strip invalid chars
      const sheetName = className.replace(/[:\\/?*[\]]/g, '').slice(0, 31);
      XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
    }

    const filename = `grades_${gradesData.assignment.title.replace(/[^a-z0-9]/gi, '_')}.xlsx`;
    XLSX.writeFile(workbook, filename);
  };

  // Get status color
  const getStatusColor = (status: string): "success" | "warning" | "error" | "info" => {
    switch (status) {
      case 'marked': return 'success';
      case 'pending': return 'warning';
      case 'review': return 'info';
      default: return 'info';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!gradesData) {
    return (
      <Box>
        <Alert severity="error">Grade report not found</Alert>
      </Box>
    );
  }

  const allClasses = getAllClasses();

  // Filter grades by selected classes
  const selectedGrades = gradesData.grades.filter(g => selectedClassIds.has(g.class_id));

  const totalLearners = selectedGrades.length;
  const markedCount = selectedGrades.filter(g => g.status === 'marked').length;

  // Get all unique sub-sections from selected grades
  const allSubsections = gradesData.rubric_sections && gradesData.rubric_sections.length > 0
    ? gradesData.rubric_sections.map(s => s.section)
    : Array.from(
        new Set(
          selectedGrades.flatMap(g => Object.keys(g.subsection_totals || {}))
        )
      ).sort((a, b) => parseInt(a) - parseInt(b));

  // Calculate overall statistics for selected classes
  const scores = selectedGrades
    .map(g => g.teacher_score ?? g.ai_score)
    .filter(s => s !== null) as number[];
  const overallAverage = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : null;

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <MuiLink
          component="button"
          variant="body1"
          onClick={() => navigate('/dashboard')}
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          <Home fontSize="small" />
          Dashboard
        </MuiLink>
        <MuiLink
          component="button"
          variant="body1"
          onClick={() => navigate('/assessments')}
          sx={{ cursor: 'pointer' }}
        >
          Assessments
        </MuiLink>
        <Typography color="text.primary">
          Summary of Results: {gradesData.assignment.title}
        </Typography>
      </Breadcrumbs>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="start">
          <Box>
            <Typography variant="h4" gutterBottom>
              <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
              Summary of Results
            </Typography>
            <Typography variant="h5" color="primary" gutterBottom>
              {gradesData.assignment.title}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Due: {new Date(gradesData.assignment.due_date).toLocaleDateString()} |
              Max Points: {gradesData.assignment.max_points}
              {gradesData.assignment.rubric_name && ` | Rubric: ${gradesData.assignment.rubric_name}`}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={handleExportCSV}
              disabled={totalLearners === 0}
            >
              Export CSV
            </Button>
            <Button
              variant="contained"
              startIcon={<FileDownload />}
              onClick={handleExportXLSX}
              disabled={totalLearners === 0}
            >
              Export XLSX
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Class Selection */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Select Classes to View:
        </Typography>

        {allClasses.length === 0 ? (
          <Alert severity="info">No submissions found for this assessment.</Alert>
        ) : (
          <>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Button
                size="small"
                startIcon={<SelectAll />}
                onClick={handleSelectAll}
                variant="outlined"
              >
                Select All
              </Button>
              <Button
                size="small"
                startIcon={<Clear />}
                onClick={handleDeselectAll}
                variant="outlined"
              >
                Deselect All
              </Button>
            </Stack>

            <Stack spacing={1}>
              {allClasses.map(classStats => (
                <Card key={classStats.class_id} variant="outlined">
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedClassIds.has(classStats.class_id)}
                          onChange={() => handleClassToggle(classStats.class_id)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {classStats.class_name}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {classStats.total_learners} learners, {classStats.marked} marked
                            {classStats.average_score !== null && ` | Avg: ${classStats.average_score.toFixed(1)}`}
                            {classStats.highest_score !== null && ` | High: ${classStats.highest_score}`}
                            {classStats.lowest_score !== null && ` | Low: ${classStats.lowest_score}`}
                          </Typography>
                        </Box>
                      }
                    />
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </>
        )}
      </Paper>

      {/* Summary Statistics */}
      {selectedClassIds.size > 0 && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.50' }}>
          <Typography variant="h6" gutterBottom>
            Summary Statistics
          </Typography>
          <Stack direction="row" spacing={4}>
            <Box>
              <Typography variant="body2" color="textSecondary">Showing</Typography>
              <Typography variant="h6">{selectedClassIds.size} {selectedClassIds.size === 1 ? 'class' : 'classes'}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="textSecondary">Total Learners</Typography>
              <Typography variant="h6">{totalLearners}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="textSecondary">Marked</Typography>
              <Typography variant="h6">{markedCount} ({totalLearners > 0 ? Math.round(markedCount / totalLearners * 100) : 0}%)</Typography>
            </Box>
            {overallAverage !== null && (
              <Box>
                <Typography variant="body2" color="textSecondary">Overall Average</Typography>
                <Typography variant="h6">{overallAverage.toFixed(1)}</Typography>
              </Box>
            )}
          </Stack>
        </Paper>
      )}

      {/* Rubric Section Overview */}
      {gradesData.rubric_sections && gradesData.rubric_sections.length > 0 && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: '#f3f6fb' }}>
          <Typography variant="h6" gutterBottom>
            Rubric Section Breakdown — {gradesData.assignment.rubric_name}
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Each learner's score is shown per section. Sections are groups of related criteria.
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            {gradesData.rubric_sections.map(sec => (
              <Chip
                key={sec.section}
                label={`§${sec.section}: ${sec.criteria_count} criteria, ${sec.max_points} pts`}
                size="small"
                color="primary"
                variant="outlined"
              />
            ))}
          </Stack>
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="textSecondary">
              Total: {gradesData.rubric_sections.reduce((sum, s) => sum + s.max_points, 0)} pts across{' '}
              {gradesData.rubric_sections.reduce((sum, s) => sum + s.criteria_count, 0)} criteria
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Grades Table */}
      {selectedClassIds.size === 0 ? (
        <Alert severity="info">Please select at least one class to view grades.</Alert>
      ) : totalLearners === 0 ? (
        <Alert severity="info">No submissions found for selected classes.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Class</TableCell>
                <TableCell>Learner Name</TableCell>
                <TableCell>Student ID</TableCell>
                {allSubsections.map(section => {
                  const rubricSec = gradesData.rubric_sections?.find(s => s.section === section);
                  const label = rubricSec
                    ? `Sec ${section}\n${rubricSec.max_points} pts`
                    : `Q${section}`;
                  return (
                    <TableCell key={section} align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={rubricSec ? `${rubricSec.criteria_count} criteria, ${rubricSec.max_points} max pts` : ''}>
                        <Typography variant="body2" fontWeight="bold">
                          {rubricSec ? `§${section}` : `Q${section}`}
                        </Typography>
                      </Tooltip>
                      {rubricSec && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          /{rubricSec.max_points}
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
                <TableCell align="right">AI Score</TableCell>
                <TableCell align="right">Teacher Score</TableCell>
                <TableCell align="right">Final Score</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedGrades.map((grade) => {
                const finalScore = grade.teacher_score ?? grade.ai_score;
                return (
                  <TableRow key={grade.submission_id} hover>
                    <TableCell>
                      <Chip label={grade.class_name} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>{grade.learner_name}</TableCell>
                    <TableCell>{grade.student_id}</TableCell>
                    {allSubsections.map(section => {
                      const subsection = grade.subsection_totals?.[section];
                      const rubricSec = gradesData.rubric_sections?.find(s => s.section === section);
                      const maxForSection = rubricSec?.max_points;
                      const isFullMarks = subsection && maxForSection && subsection.score === maxForSection;
                      const isPartial = subsection && subsection.score > 0;
                      return (
                        <TableCell key={section} align="center">
                          {subsection ? (
                            <Tooltip title={`${section}: ${subsection.score} / ${subsection.max_score} pts`}>
                              <Chip
                                size="small"
                                label={subsection.score}
                                color={isFullMarks ? 'success' : isPartial ? 'primary' : 'default'}
                                variant={subsection.score > 0 ? 'filled' : 'outlined'}
                              />
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="textSecondary">—</Typography>
                          )}
                        </TableCell>
                      );
                    })}
                    <TableCell align="right">
                      {grade.ai_score !== null ? grade.ai_score.toFixed(1) : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {grade.teacher_score !== null ? grade.teacher_score.toFixed(1) : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">
                        {finalScore !== null ? finalScore.toFixed(1) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={grade.status}
                        size="small"
                        color={getStatusColor(grade.status)}
                      />
                    </TableCell>
                    <TableCell>
                      {grade.submitted_at ? new Date(grade.submitted_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View submission">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/submissions/${grade.submission_id}/mark`)}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AssessmentGradesPage;
